import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { movies, categories } from '@/mocks/movies';

const CATEGORY_NAMES: Record<string, string> = {};
categories.forEach((c) => { CATEGORY_NAMES[c.key] = c.name; });

export default function Detail() {
  const { movieId } = useParams<{ movieId: string }>();
  const movie = movies.find((m) => m.id === Number(movieId));

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-600 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-100 mb-2">影片未找到</h2>
            <p className="text-foreground-500 mb-4">抱歉，您查找的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-950 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span className="text-sm">返回首页</span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const jsonLdMovie = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.englishTitle,
    description: movie.description,
    dateCreated: String(movie.year),
    director: { '@type': 'Person', name: movie.director },
    actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
    duration: movie.duration,
    countryOfOrigin: { '@type': 'Country', name: movie.region },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(movie.rating), bestRating: '10' },
    genre: movie.tags,
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.7777yingyuan.com/' },
      { '@type': 'ListItem', position: 2, name: CATEGORY_NAMES[movie.category] || movie.category, item: `https://www.7777yingyuan.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLdMovie)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        {/* Banner */}
        <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={movie.imageUrl}
            alt={movie.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-transparent" />
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 -mt-32 relative z-10">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: CATEGORY_NAMES[movie.category] || movie.category, path: `/category/${movie.category}` },
                { name: movie.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Poster */}
            <div className="shrink-0 w-full sm:w-[280px]">
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={movie.imageUrl}
                  alt={movie.title}
                  title={`${movie.title} - 7777影院高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-1">
                {movie.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1 bg-primary-500/10 text-primary-500 text-xs font-bold px-2.5 py-1 rounded-full">
                  <i className="ri-star-fill text-[10px]"></i>
                  {movie.rating}
                </span>
                {movie.isNew && (
                  <span className="bg-accent-500 text-white text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">NEW</span>
                )}
                {movie.isHot && (
                  <span className="bg-primary-500 text-background-950 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">HOT</span>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                <div>
                  <span className="text-xs text-foreground-600">导演</span>
                  <p className="text-sm text-foreground-200 mt-0.5">{movie.director}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-600">年份</span>
                  <p className="text-sm text-foreground-200 mt-0.5">{movie.year}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-600">地区</span>
                  <p className="text-sm text-foreground-200 mt-0.5">{movie.region}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-600">时长</span>
                  <p className="text-sm text-foreground-200 mt-0.5">{movie.duration}</p>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <span className="text-xs text-foreground-600">类型</span>
                  <p className="text-sm text-foreground-200 mt-0.5">{movie.tags.join(' / ')}</p>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-xs text-foreground-600">主演</span>
                <p className="text-sm text-foreground-200 mt-1">{movie.cast.join('、')}</p>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>
                  <span className="text-sm">立即播放</span>
                </Link>
                <button className="inline-flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-300 font-medium px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border border-background-200/50">
                  <i className="ri-heart-line text-lg"></i>
                  <span className="text-sm">收藏</span>
                </button>
              </div>

              {/* Description */}
              <div className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/50">
                <h2 className="text-base font-semibold text-foreground-100 mb-2">剧情简介</h2>
                <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
              </div>
            </div>
          </div>

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="py-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full" />
                <h2 className="text-xl font-heading font-bold text-foreground-50">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}